import type { ReactElement, ReactNode } from 'react';
import type { CardLabel, CustomFieldType, Reconciliation } from '@molt/shared';
import { LabelChip, Notice } from '@/components';
import { hasReconciliationConsequences } from '@/lib/cross-board-move';
import { customFieldTypeLabel } from '@/lib/custom-field-type';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface MoveReconciliationReportProps {
  reconciliation: Reconciliation;
  cardsMoved?: number | undefined;
  summary?: string | undefined;
  tone?: 'notice' | 'result' | undefined;
}

export const reconciliationHasConsequences = hasReconciliationConsequences;

export function MoveReconciliationReport({
  reconciliation,
  cardsMoved,
  summary: explicitSummary,
  tone = 'result',
}: MoveReconciliationReportProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const hasConsequences = reconciliationHasConsequences(reconciliation);
  const hasReportRows = reconciliationHasReportRows(reconciliation);
  /* ⚠ The count is passed TWICE — into `values` for the `{count}` slot and as
     the third argument that selects the arm. `interfaceMessage` defaults
     `count` to 0, so omitting the third argument renders `2 cards moved` at
     one, with nothing red anywhere. */
  const summary =
    explicitSummary ??
    (cardsMoved === undefined
      ? t('copyMove.reconciliation.complete')
      : t('copyMove.reconciliation.cardsMoved', { count: cardsMoved }, cardsMoved));

  if (!hasReportRows) {
    return (
      <Notice
        tone="info"
        title={summary}
        body={t('copyMove.reconciliation.nothingChanged')}
      />
    );
  }

  return (
    <Notice
      tone={tone === 'notice' && hasConsequences ? 'warn' : 'info'}
      title={summary}
      body={
        <div className="flex flex-col gap-3">
          <ReportGroup
            title={t('copyMove.reconciliation.labels')}
            rows={[
              ...reconciliation.labels.applied.map((label) => ({
                id: `label-applied-${label.id}`,
                content: (
                  <>
                    <span>{t('copyMove.reconciliation.labelApplied')}</span>
                    <LabelView label={label} />
                  </>
                ),
              })),
              ...reconciliation.labels.created.map((label) => ({
                id: `label-created-${label.id}`,
                content: (
                  <>
                    <span>{t('copyMove.reconciliation.labelCreated')}</span>
                    <LabelView label={label} />
                  </>
                ),
              })),
              ...reconciliation.labels.dropped.map((label) => ({
                id: `label-dropped-${label.id}`,
                content: (
                  <>
                    <span>{t('copyMove.reconciliation.labelDropped')}</span>
                    <LabelView label={label} />
                  </>
                ),
              })),
            ]}
          />

          <ReportGroup
            title={t('copyMove.reconciliation.members')}
            rows={reconciliation.members.removed.map((member) => ({
              id: `member-removed-${member.id}`,
              content: (
                <>
                  <span>{t('copyMove.reconciliation.memberRemoved')}</span>
                  <span className="font-medium text-ink">{member.displayName}</span>
                </>
              ),
            }))}
          />

          <ReportGroup
            title={t('copyMove.reconciliation.customFields')}
            rows={[
              ...reconciliation.customFields.applied.map((field) => ({
                id: `custom-field-applied-${field.source.id}-${field.destination.id}`,
                content: (
                  <>
                    <span>{t('copyMove.reconciliation.fieldMatched')}</span>
                    <span className="font-medium text-ink">
                      {t('copyMove.reconciliation.fieldMatchedNames', {
                        source: field.source.name,
                        destination: field.destination.name,
                      })}
                    </span>
                    <FieldType value={field.destination.type} />
                  </>
                ),
              })),
              ...reconciliation.customFields.created.map((field) => ({
                id: `custom-field-created-${field.id}`,
                content: (
                  <>
                    <span>{t('copyMove.reconciliation.fieldCreated')}</span>
                    <span className="font-medium text-ink">{field.name}</span>
                    <FieldType value={field.type} />
                  </>
                ),
              })),
              ...reconciliation.customFields.optionsCreated.map((option) => ({
                id: `custom-field-option-${option.definitionId}-${option.optionId}`,
                content: (
                  <>
                    <span>{t('copyMove.reconciliation.optionCreated')}</span>
                    <span className="font-medium text-ink">{option.label}</span>
                  </>
                ),
              })),
              ...reconciliation.customFields.notAppliedToExisting.map((field) => ({
                id: `custom-field-not-applied-${field.source.id}-${field.existing.id}`,
                content: (
                  <>
                    <span>{t('copyMove.reconciliation.fieldNotApplied')}</span>
                    <span className="font-medium text-ink">
                      {t('copyMove.reconciliation.fieldConflict', {
                        source: field.source.name,
                        existing: field.existing.name,
                      })}
                    </span>
                    <FieldType value={field.existing.type} />
                  </>
                ),
              })),
            ]}
          />
        </div>
      }
    />
  );
}

function reconciliationHasReportRows(reconciliation: Reconciliation): boolean {
  return (
    reconciliation.labels.applied.length > 0 ||
    reconciliation.labels.created.length > 0 ||
    reconciliation.labels.dropped.length > 0 ||
    reconciliation.members.removed.length > 0 ||
    reconciliation.customFields.applied.length > 0 ||
    reconciliation.customFields.created.length > 0 ||
    reconciliation.customFields.optionsCreated.length > 0 ||
    reconciliation.customFields.notAppliedToExisting.length > 0
  );
}

function ReportGroup({
  title,
  rows,
}: {
  title: string;
  rows: readonly { id: string; content: ReactNode }[];
}): ReactElement | null {
  if (rows.length === 0) return null;

  return (
    <section className="flex flex-col gap-1.5">
      <h3 className="text-sm font-semibold text-ink">{title}</h3>
      <ul className="flex flex-col gap-1">
        {rows.map((row) => (
          <li key={row.id} className="flex flex-wrap items-center gap-2">
            {row.content}
          </li>
        ))}
      </ul>
    </section>
  );
}

function LabelView({ label }: { label: CardLabel }): ReactElement {
  return <LabelChip colour={label.colour} name={label.name} size="sm" />;
}

/**
 * ⚠ THIS PRINTED A RAW WIRE ENUM AS UI TEXT. `reconciliationSchema` types all
 * four of these fields as `customFieldTypeSchema`, so the value is a
 * `CustomFieldType` and `customFieldTypeLabel` applies exactly; the prop is
 * narrowed from `string` to say so. In English nothing moves — `'text'` and
 * `'Text'` both paint `TEXT` under the `uppercase` class.
 *
 * ⚠ That `uppercase` is a LANGUAGE-SPECIFIC transform (Turkish `I` lowercases
 * to a dotless `ı`) and is left exactly as it is: changing it is a visual
 * change, not a routing one. Returned as a residual instead.
 */
function FieldType({ value }: { value: CustomFieldType }): ReactElement {
  const { t } = useInterfaceLanguage();
  return (
    <span className="text-xs uppercase text-ink-muted">
      {customFieldTypeLabel(t, value)}
    </span>
  );
}
